import { useState, useRef, useEffect } from 'react';
import { css } from '@emotion/react';
import { Icon } from '@frontend/icons';
import { SuperTextarea, SuperTextAreaRef } from '@frontend/super-textarea';
import { theme } from '@frontend/theme';
import { Chat, Avatar, Button } from '@frontend/design-system';

interface Message {
  id: string;
  text: string;
  direction: 'inbound' | 'outbound';
  timestamp: Date;
  senderName: string;
  error?: string;
}

export interface ChatWindowProps {
  initialMessages?: Message[];
  onSendMessage: (message: string) => Promise<{ text: string; error?: string }>;
  placeholder?: string;
  userName?: string;
  aiName?: string;
  disabled?: boolean;
}

const chatContainerStyle = css`
  display: flex;
  flex-direction: column;
  height: 100%;
  width: 100%;
  max-width: 800px;
  margin: 0 auto;
`;

const messagesContainerStyle = css`
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  padding: ${theme.spacing(2)};
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing(2)};
`;

export const ChatWindow = ({
  initialMessages = [],
  onSendMessage,
  placeholder = 'Type a message...',
  userName = 'You',
  aiName = 'AI Receptionist',
  disabled = false,
}: ChatWindowProps) => {
  const [messages, setMessages] = useState<Message[]>(initialMessages);
  const [inputValue, setInputValue] = useState('');
  const [isSending, setIsSending] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const textareaRef = useRef<SuperTextAreaRef>(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const handleSubmit = async (text: string) => {
    if (!text.trim() || isSending) return;

    const userMessage: Message = {
      id: `msg-${Date.now()}`,
      text: text.trim(),
      direction: 'inbound',
      timestamp: new Date(),
      senderName: userName,
    };

    setMessages((prev) => [...prev, userMessage]);
    setInputValue('');
    setIsSending(true);

    try {
      const response = await onSendMessage(text.trim());

      const aiMessage: Message = {
        id: `msg-${Date.now()}-response`,
        text: response.text,
        direction: 'outbound',
        timestamp: new Date(),
        senderName: aiName,
        error: response.error,
      };

      setMessages((prev) => [...prev, aiMessage]);
    } catch (error) {
      const errorMessage: Message = {
        id: `msg-${Date.now()}-error`,
        text: 'Failed to send message. Please try again.',
        direction: 'outbound',
        timestamp: new Date(),
        senderName: aiName,
        error: 'Network error',
      };

      setMessages((prev) => [...prev, errorMessage]);
    } finally {
      setIsSending(false);
      // Use setTimeout to ensure focus happens after React state updates complete
      setTimeout(() => {
        textareaRef.current?.focus();
      }, 0);
    }
  };

  return (
    <div css={chatContainerStyle}>
      <div css={messagesContainerStyle}>
        {messages.map((message) => {
          const avatar = (
            <Avatar
              size='small'
              title={message.senderName}
              name={message.senderName}
              isAiAssistant={message.direction === 'outbound'}
            />
          );

          return (
            <Chat
              key={message.id}
              direction={message.direction}
              timestamp={message.timestamp.toLocaleTimeString(undefined, { timeStyle: 'short' })}
              senderName={message.senderName}
              avatar={avatar}
              error={message.error}
            >
              <Chat.Bubble
                text={message.text}
                backgroundColor={message.direction === 'outbound' ? theme.colors.success5 : undefined}
              />
            </Chat>
          );
        })}
        {isSending && (
          <Chat
            direction='outbound'
            timestamp={new Date().toLocaleTimeString(undefined, { timeStyle: 'short' })}
            senderName={aiName}
            avatar={<Avatar size='small' title={aiName} name={aiName} isAiAssistant />}
            isSending
          >
            <Chat.Bubble text='Typing...' backgroundColor={theme.colors.success5} />
          </Chat>
        )}
        <div ref={messagesEndRef} />
      </div>

      <SuperTextarea
        ref={textareaRef}
        value={inputValue}
        onChange={setInputValue}
        onSubmit={handleSubmit}
        placeholder={placeholder}
        disabled={disabled || isSending}
        disableEmojis
        minRows={1}
        hasSubmit
        SubmitComponent={SubmitButton}
        submitButtonLabel={<Icon name='send' />}
        styleProp={css`
          resize: none;
        `}
      />
    </div>
  );
};

const SubmitButton = ({ disabled, onClick }: { disabled: boolean; onClick: React.FormEventHandler }) => (
  <Button variant='primary' disabled={disabled} onClick={onClick} trackingId='chat-send-button' iconName='send' />
);
